import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { createPublicLead, startPublicLead } from "../api/leadsApi";

/* Lead CTA modal — SHIP B.6, guest-capable since Aufgabe 11 (06.10.2026).
 *
 * Sits underneath the Financing Calculator on the Listing Detail Page.
 * Works WITHOUT an account. Name, e-mail and phone are all mandatory
 * (operator decision). Protection, mirrored 1:1 on the backend:
 *   - honeypot field (hidden, must stay empty)
 *   - server-issued one-shot token = the submit-time window
 *   - explicit, versioned consent
 *   - duplicate + per-IP limits are enforced server-side
 * No captcha (explicitly ruled out).
 *
 * The success screen does NOT claim the request was forwarded: the real
 * partner transfer is not built (no API documentation), so it names the
 * INTENDED partner and says a manual review follows.
 */

const PARTNER_BRANDS = {
  infina: "Infina",
  santander: "Santander",
  finanzfuchs: "Finanzfuchs",
};
function partnerLabel(slug) {
  if (slug && PARTNER_BRANDS[slug]) return PARTNER_BRANDS[slug];
  return i18n.t(`mobile:lead.partners.${slug || "manual_review"}`, {
    defaultValue: i18n.t("mobile:lead.partners.manual_review"),
  });
}

export default function LeadModal({
  open,
  onClose,
  card,
  financingInputs,
  financingOutput,
}) {
  const { t } = useTranslation(["mobile", "common", "auth"]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);

  // Fetch the one-shot token as soon as the form opens — its creation
  // time IS the submit-time window, so it must not come from the client.
  useEffect(() => {
    if (!open || ticket) return;
    let cancelled = false;
    startPublicLead()
      .then((d) => {
        if (!cancelled) setTicket(d);
      })
      .catch(() => {
        if (!cancelled) setError(t("mobile:lead.createFailed"));
      });
    return () => {
      cancelled = true;
    };
  }, [open, ticket, t]);

  if (!open) return null;

  const vertical = card?.vertical || "immo";

  const canSubmit =
    name.trim().length >= 2 &&
    /.+@.+\..+/.test(email) &&
    phone.replace(/[^\d]/g, "").length >= 6 &&
    accepted &&
    Boolean(ticket?.challenge) &&
    !isSubmitting;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const data = await createPublicLead({
        challenge: ticket.challenge,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        consent: true,
        consent_policy_version: ticket.consent_policy_version,
        website,
        vertical,
        lead_type: "financing",
        listing_ulid: card?.ulid || card?.id || null,
        financing_inputs: financingInputs || null,
        calculator_output: financingOutput
          ? {
              monthly_rate: financingOutput.monthly_rate,
              total_interest: financingOutput.total_interest,
              total_cost: financingOutput.total_cost,
              effective_rate: financingOutput.effective_rate,
            }
          : null,
      });
      setResult(data);
      toast.success(
        t("mobile:lead.createdToast", {
          partner: partnerLabel(data?.partner_routing?.partner),
        }),
      );
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      const status = err?.response?.status;
      let msg = err?.response?.data?.error?.message || err?.message;
      if (status === 409 || code === "lead_duplicate")
        msg = t("mobile:lead.duplicate");
      else if (status === 429) msg = t("mobile:lead.rateLimited");
      else if (code === "consent_version_mismatch")
        msg = t("mobile:lead.consentReload");
      setError(msg || t("mobile:lead.createFailed"));
      // A consumed token can never be reused — fetch a fresh one.
      setTicket(null);
      toast.error(t("mobile:lead.createFailed"), { description: String(msg) });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    setResult(null);
    setError(null);
    setName("");
    setEmail("");
    setPhone("");
    setWebsite("");
    setAccepted(false);
    setTicket(null);
    onClose?.();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="lead-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-zinc-950/85 p-4"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h3 className="text-base font-medium text-zinc-100">
            {result ? t("mobile:lead.submittedTitle") : t("mobile:detail.requestFinancing")}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            data-testid="lead-modal-close"
            className="text-zinc-400 hover:text-zinc-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="space-y-4 p-5">
            <div
              data-testid="lead-modal-summary"
              className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2.5 text-xs text-zinc-400 space-y-1"
            >
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                {t("mobile:lead.fromCalculator")}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 tabular-nums text-zinc-300">
                <span>{t("mobile:finance.purchasePrice")}</span>
                <span className="text-right">
                  {card?.price?.toLocaleString("de-AT")} €
                </span>
                {financingInputs?.down_payment !== undefined && (
                  <>
                    <span>{t("mobile:finance.downPayment")}</span>
                    <span className="text-right">
                      {Number(financingInputs.down_payment).toLocaleString(
                        "de-AT",
                      )}{" "}
                      €
                    </span>
                  </>
                )}
                {financingInputs?.interest_rate !== undefined && (
                  <>
                    <span>{t("mobile:finance.interest")}</span>
                    <span className="text-right">
                      {(financingInputs.interest_rate * 100)
                        .toFixed(2)
                        .replace(".", ",")}{" "}
                      %
                    </span>
                  </>
                )}
                {financingInputs?.term_months !== undefined && (
                  <>
                    <span>{t("mobile:finance.term")}</span>
                    <span className="text-right">
                      {t("mobile:lead.months", { count: financingInputs.term_months })}
                    </span>
                  </>
                )}
                {financingOutput?.monthly_rate !== undefined && (
                  <>
                    <span className="text-amber-300">{t("mobile:finance.monthlyRate")}</span>
                    <span className="text-right text-amber-300">
                      {Math.round(financingOutput.monthly_rate).toLocaleString(
                        "de-AT",
                      )}{" "}
                      €
                    </span>
                  </>
                )}
              </div>
            </div>

            <p
              data-testid="lead-modal-guest-hint"
              className="text-xs text-zinc-400"
            >
              {t("mobile:lead.guestHint")}
            </p>

            <Field
              label={t("mobile:lead.nameLabel")}
              value={name}
              onChange={setName}
              testid="lead-modal-name"
              required
            />
            <Field
              label={t("auth:register.email")}
              value={email}
              onChange={setEmail}
              type="email"
              testid="lead-modal-email"
              required
            />
            <Field
              label={t("mobile:lead.phoneLabel")}
              value={phone}
              onChange={setPhone}
              type="tel"
              testid="lead-modal-phone"
              required
            />

            {/* Honeypot — invisible to people, irresistible to bots. */}
            <div className="hidden" aria-hidden="true">
              <label>
                Website
                <input
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  data-testid="lead-modal-honeypot"
                />
              </label>
            </div>

            <label
              className="flex items-start gap-2 text-xs text-zinc-400"
              data-testid="lead-modal-consent"
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 accent-amber-500"
              />
              <span>{t("mobile:lead.consent")}</span>
            </label>

            {error && (
              <div
                data-testid="lead-modal-error"
                className="rounded-lg border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-xs text-rose-200"
              >
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-200 hover:border-zinc-500"
              >
                {t("common:ui.cancel")}
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="lead-modal-submit"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isSubmitting && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                {t("mobile:lead.submitCta")}
              </button>
            </div>
          </form>
        ) : (
          <div data-testid="lead-modal-success" className="space-y-4 p-5">
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
              ✓ {t("mobile:lead.successRecorded")}
            </div>
            <div
              data-testid="lead-modal-handoff-note"
              className="rounded-xl border border-zinc-700 bg-zinc-950/60 px-4 py-3 text-xs text-zinc-300"
            >
              {result?.partner_routing?.partner === "manual_review" ? (
                t("mobile:lead.noPartnerYet")
              ) : (
                <>
                  <span className="text-zinc-500">
                    {t("mobile:lead.intendedPartner")}:{" "}
                  </span>
                  <span className="font-semibold text-zinc-100">
                    {partnerLabel(result?.partner_routing?.partner)}
                  </span>
                  <div className="mt-1 text-zinc-400">
                    {t("mobile:lead.handoffPendingNote")}
                  </div>
                </>
              )}
            </div>
            <dl className="space-y-1 rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2.5 text-xs text-zinc-300">
              <Row k="Lead-ID" v={result.lead_ulid} />
              <Row k="Status" v={result.status} />
              <Row k="Land" v={result.country} />
              <Row k="Partner-Typ" v={result?.partner_routing?.partner_type} />
              <Row k="Reason" v={result?.partner_routing?.match_reason} />
            </dl>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-amber-400"
              >
                {t("mobile:common.done")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block space-y-1" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
        {required && <span className="text-rose-400"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 focus:outline-none"
      />
    </label>
  );
}

function Row({ k, v }) {
  if (v == null || v === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-zinc-500">{k}</span>
      <span className="tabular-nums text-zinc-200 text-right break-all">
        {String(v)}
      </span>
    </div>
  );
}
